import { useEffect, useState } from "react";

/**
 * The single breakpoint that decides which of a page's two views is mounted.
 *
 * It is deliberately the ONLY place this number lives on the React side. The
 * stylesheets below it still carry their own sub-breakpoints (560, 620, 760,
 * 820...) and the desktop sheets carry theirs (1000, 1080, 1280) - those are
 * responsive detail *within* a view. This one decides which view exists at
 * all, so it has to agree with the split that produced the two stylesheets.
 */
export const MOBILE_MAX = 900;
export const MOBILE_QUERY = `(max-width: ${MOBILE_MAX}px)`;

export type ViewName = "desktop" | "mobile";

function read(): ViewName {
  if (typeof window === "undefined" || !window.matchMedia) return "desktop";
  return window.matchMedia(MOBILE_QUERY).matches ? "mobile" : "desktop";
}

/**
 * Which view the current viewport wants, kept live.
 *
 * Crossing the breakpoint - a tablet rotating, a desktop window dragged
 * narrow - returns a different name, which is what drives PageShell to tear
 * the current view down and mount the other one.
 */
export function useView(): ViewName {
  const [view, setView] = useState<ViewName>(read);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setView(mq.matches ? "mobile" : "desktop");

    // Safari < 14 only has the deprecated listener API.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    // the media query can have flipped between first render and this effect
    onChange();

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return view;
}
